import { Head } from 'vite-react-ssg';
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  MessageSquare,
  Code2,
  Server,
  ExternalLink,
  ArrowRight,
  BookOpen,
  FileText,
  CreditCard,
  Info,
  Puzzle,
  Scale,
  Sparkles,
} from "lucide-react";
import V4Tracker from "@/components/V4Tracker";
import LastUpdated from "@/components/LastUpdated";
import CostComparator from "@/components/CostComparator";
import { DEEPSEEK_PRICING, getRate } from "@/lib/deepseek-pricing";

const TITLE = "DeepSeek AI: Chat, Models & Independent Guides";
const DESCRIPTION =
  "What DeepSeek is, how to use the official chat, and independent guides to the V4 models, API pricing and login — with a cross-provider cost comparator. Reviewed 9 September 2026.";

const MODEL_ROWS = [
  {
    name: DEEPSEEK_PRICING.models["v4-flash"].label,
    role: "Fast everyday model. Text plus an experimental vision variant.",
    rate: getRate("v4-flash"),
    href: "/deepseek-v4-flash-review",
  },
  {
    name: DEEPSEEK_PRICING.models["v4-pro"].label,
    role: "Flagship reasoning and coding model, for harder multi-step work.",
    rate: getRate("v4-pro"),
    href: "/deepseek-v4",
  },
];

const Index = () => {
  const faqItems = [
    {
      question: "Is this the official DeepSeek website?",
      answer:
        "No. This is an independent guide and community resource. We are not affiliated with, sponsored by, or endorsed by Hangzhou DeepSeek Artificial Intelligence Co., Ltd. The official site is deepseek.com and the official chat is chat.deepseek.com.",
    },
    {
      question: "What is DeepSeek AI?",
      answer:
        "DeepSeek AI is a Chinese AI research company that builds large language models. Its current main model is DeepSeek-V4.1-Flash (API name deepseek-flash), generally available since 9 September 2026, with native image understanding built in. DeepSeek-V4-Pro (API name deepseek-v4-pro) remains available as the stronger, more expensive reasoning model; DeepSeek confirmed its API service continues after 14 September 2026 with unchanged billing. The earlier V3.1 and R1 models are still widely used and open-weight, and the old deepseek-chat and deepseek-reasoner API endpoints were retired on 24 July 2026.",
    },
    {
      question: "How do I use DeepSeek for free?",
      answer:
        "Sign in at chat.deepseek.com — the official chat has a free tier and runs the current V4 models. You can also try the free demo chat on this site, which runs DeepSeek V3.1 on community routes (falling back to R1 when a route is busy). Our demo is not the official service and does not run V4.",
    },
    {
      question: "What does the DeepSeek API cost?",
      answer: `Per million tokens, V4-Flash is $${getRate("v4-flash").inputCacheMiss} input (cache miss) and $${getRate("v4-flash").output} output; V4-Pro is $${getRate("v4-pro").inputCacheMiss} input and $${getRate("v4-pro").output} output. Rates last verified against DeepSeek's official rate card on ${DEEPSEEK_PRICING.lastVerified}. Our pricing page keeps the full table, cache-hit rates and price history.`,
    },
    {
      question: "How does DeepSeek compare to ChatGPT, Claude and Gemini?",
      answer:
        "On published API rates DeepSeek is far cheaper per million tokens than the GPT, Claude and Gemini flagships, and it is competitive on coding and reasoning benchmarks. The cost comparator on this page shows what the same monthly workload costs on each provider's own published rates, with a link to every rate card.",
    },
    {
      question: "Can I run DeepSeek locally?",
      answer:
        "Yes. DeepSeek publishes open weights on GitHub and Hugging Face, and they run locally with Ollama, vLLM, llama.cpp or LM Studio. Distilled variants fit a single 24GB GPU; full models need multi-GPU hardware.",
    },
    {
      question: "Is DeepSeek safe to use?",
      answer:
        "It depends on your data and your jurisdiction. The hosted service processes prompts on DeepSeek's infrastructure in China, several governments restrict it on official devices, and self-hosting the open weights avoids sending data anywhere. Our safety page walks through the specifics.",
    },
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        name: "The Independent DeepSeek Guide",
        url: "https://deepseek.ai",
        description:
          "Independent guide to DeepSeek AI: what it is, how to use the official chat, the V4 models, API pricing and a cross-provider cost comparator. Not affiliated with DeepSeek.",
      },
      {
        "@type": "Organization",
        name: "The Independent DeepSeek Guide",
        url: "https://deepseek.ai",
        logo: "https://deepseek.ai/og-image.png",
        description:
          "Independent editorial desk publishing source-checked guides, pricing and news about DeepSeek AI. Not affiliated with Hangzhou DeepSeek Artificial Intelligence Co., Ltd.",
      },
      {
        "@type": "FAQPage",
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
    ],
  };

  return (
    <>
      <Head>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href="https://deepseek.ai/" />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content="https://deepseek.ai/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Head>

      <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        {/* Independence disclaimer */}
        <div className="bg-primary/10 border-b border-primary/20">
          <div className="container mx-auto px-4 py-3 text-center">
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-2 flex-wrap">
              <Info className="h-4 w-4 text-primary" />
              <span className="font-medium">This is an independent fan site.</span>
              <a
                href="https://www.deepseek.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                Visit official DeepSeek.com <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>
        </div>

        {/* Hero: answer the query, then route the visitor */}
        <section className="container mx-auto px-4 pt-14 pb-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              DeepSeek AI: <span className="text-primary">Chat, Models &amp; Independent Guides</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
              DeepSeek is a Chinese AI lab whose large language models are unusually cheap to run and
              open-weight. Its current models are <strong>DeepSeek-V4-Pro</strong> for hard reasoning and
              coding and <strong>DeepSeek-V4.1-Flash</strong> for fast everyday use; the earlier V3.1 and R1
              models are still open and popular but no longer the flagship line.
            </p>
            <p className="text-base text-muted-foreground max-w-3xl mx-auto mb-10">
              This is an independent guide, not the official service. We keep the model list, API rates and
              login steps checked against DeepSeek's own documentation and print the review date on every page.
            </p>

            {/* Two honest ways to use DeepSeek */}
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto text-left">
              <Card className="bg-white border-2 border-primary/30 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="h-5 w-5 text-primary" />
                    <h2 className="text-lg font-semibold">Use the official DeepSeek chat</h2>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Free tier, runs the current V4 models, made by DeepSeek. You sign in on their site.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild size="sm">
                      <a href="https://chat.deepseek.com" target="_blank" rel="noopener noreferrer">
                        Open chat.deepseek.com <ExternalLink className="ml-2 h-3.5 w-3.5" />
                      </a>
                    </Button>
                    <Button asChild size="sm" variant="outline">
                      <Link to="/login">Login help</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-slate-200 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <h2 className="text-lg font-semibold">Try our free demo chat</h2>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    No signup, but it is <strong>not</strong> the official service: it runs DeepSeek V3.1 on
                    free community routes (R1 fallback) and does not run V4.
                  </p>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/chat">
                      Open the demo chat <ArrowRight className="ml-2 h-3.5 w-3.5" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 flex justify-center">
              <LastUpdated date="2026-09-09" />
            </div>
          </div>
        </section>

        <V4Tracker />

        {/* Models and costs at a glance */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground mb-3">
              DeepSeek models and what they cost
            </h2>
            <p className="text-muted-foreground mb-8">
              Rates are per million tokens, last verified against DeepSeek's official rate card on{" "}
              {DEEPSEEK_PRICING.lastVerified}.{" "}
              <a
                href={DEEPSEEK_PRICING.source}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Official pricing page
              </a>
              .
            </p>

            <div className="overflow-x-auto bg-white rounded-lg shadow-md">
              <table className="w-full text-sm">
                <caption className="sr-only">Current DeepSeek models and API rates per million tokens</caption>
                <thead>
                  <tr className="border-b text-left">
                    <th scope="col" className="p-4 font-semibold">Model</th>
                    <th scope="col" className="p-4 font-semibold">Best for</th>
                    <th scope="col" className="p-4 font-semibold">Input (cache miss)</th>
                    <th scope="col" className="p-4 font-semibold">Output</th>
                    <th scope="col" className="p-4 font-semibold">Guide</th>
                  </tr>
                </thead>
                <tbody>
                  {MODEL_ROWS.map((m) => (
                    <tr key={m.name} className="border-b last:border-0">
                      <td className="p-4 font-medium">{m.name}</td>
                      <td className="p-4 text-muted-foreground">{m.role}</td>
                      <td className="p-4">${m.rate.inputCacheMiss}</td>
                      <td className="p-4">${m.rate.output}</td>
                      <td className="p-4">
                        <Link to={m.href} className="text-primary hover:underline inline-flex items-center gap-1">
                          Read <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-sm text-muted-foreground mt-4">
              Cache hits cost a fraction of that. Full table, price history and the retired V3/R1 endpoints are
              on the <Link to="/pricing" className="text-primary hover:underline">DeepSeek pricing page</Link>.
            </p>
          </div>
        </section>

        {/* The tool: cross-provider cost comparator */}
        <section className="container mx-auto px-4 pb-16">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 mb-3">
              <Scale className="h-6 w-6 text-primary" />
              <h2 className="text-3xl font-bold text-foreground">
                Compare what your workload really costs
              </h2>
            </div>
            <p className="text-muted-foreground mb-8 max-w-3xl">
              Pick a workload and see the monthly bill on DeepSeek V4.1-Flash and V4-Pro next to the GPT,
              Claude and Gemini models people actually weigh them against — all from published rate cards,
              each row linking to the provider's own price page.
            </p>
            <CostComparator />
          </div>
        </section>

        {/* Guides */}
        <section className="container mx-auto px-4 pb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground mb-3">Independent DeepSeek guides</h2>
            <p className="text-muted-foreground mb-8 max-w-3xl">
              Source-checked walkthroughs for the questions people actually arrive with.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow">
                <CardContent className="pt-8 pb-8">
                  <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-5">
                    <Code2 className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">DeepSeek API guide</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Keys, endpoints, model IDs, prompt caching and code examples — including what changed when
                    the old chat and reasoner endpoints were retired.
                  </p>
                  <Link to="/deepseek-api" className="text-primary hover:underline inline-flex items-center gap-1">
                    Read the DeepSeek API guide <ArrowRight className="h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>

              <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow">
                <CardContent className="pt-8 pb-8">
                  <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-5">
                    <Server className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Run DeepSeek locally</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Open weights, hardware you need, and step-by-step setup with Ollama, vLLM and llama.cpp.
                  </p>
                  <Link to="/docs" className="text-primary hover:underline inline-flex items-center gap-1">
                    Local deployment guide <ArrowRight className="h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>

              <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow">
                <CardContent className="pt-8 pb-8">
                  <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-5">
                    <BookOpen className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Is DeepSeek safe?</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Where your prompts go, which governments restrict it, and how self-hosting changes the answer.
                  </p>
                  <Link to="/is-deepseek-safe" className="text-primary hover:underline inline-flex items-center gap-1">
                    Privacy &amp; safety <ArrowRight className="h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <Link to="/deepseek-vs-chatgpt" className="group">
                <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow h-full">
                  <CardContent className="p-6 text-center">
                    <Scale className="h-8 w-8 text-primary mx-auto mb-3" />
                    <h3 className="font-semibold text-sm">DeepSeek vs ChatGPT</h3>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/blog" className="group">
                <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow h-full">
                  <CardContent className="p-6 text-center">
                    <BookOpen className="h-8 w-8 text-primary mx-auto mb-3" />
                    <h3 className="font-semibold text-sm">News &amp; guides</h3>
                  </CardContent>
                </Card>
              </Link>
              <a href="https://github.com/deepseek-ai" target="_blank" rel="noopener noreferrer" className="group">
                <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow h-full">
                  <CardContent className="p-6 text-center">
                    <FileText className="h-8 w-8 text-primary mx-auto mb-3" />
                    <h3 className="font-semibold text-sm">DeepSeek papers &amp; code (GitHub)</h3>
                  </CardContent>
                </Card>
              </a>
              <Link to="/pricing" className="group">
                <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow h-full">
                  <CardContent className="p-6 text-center">
                    <CreditCard className="h-8 w-8 text-primary mx-auto mb-3" />
                    <h3 className="font-semibold text-sm">Pricing guide</h3>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="container mx-auto px-4 pb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-foreground mb-10">
              DeepSeek: frequently asked questions
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {faqItems.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white border border-slate-200 rounded-lg px-6"
                >
                  <AccordionTrigger className="text-left font-semibold hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Secondary: browser extension */}
        <section className="container mx-auto px-4 pb-20">
          <Card className="bg-white border border-slate-200 shadow-sm max-w-3xl mx-auto">
            <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
                <Puzzle className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold mb-1">Prefer a sidebar in your browser?</h2>
                <p className="text-sm text-muted-foreground">
                  Our free Chrome extension puts an AI sidebar next to any page. Built by us, not by DeepSeek.
                </p>
              </div>
              <Button asChild variant="outline" className="shrink-0">
                <a
                  href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Add to Chrome <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </CardContent>
          </Card>
        </section>
      </main>
    </>
  );
};

export default Index;
